import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import bookingService from '../../../../../../services/booking.service';
import { confirmDelete, showError, showSuccess } from '../../../../../../utils/ecxSwal';
import { ClinicInfoPanel } from '../../info/clinicInfoParts';
import ClinicSettingsSectionHeader from '../../subparts/ClinicSettingsSectionHeader';
import ClinicSettingsEmpty from '../../subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsFormFooter from '../../subparts/ClinicSettingsFormFooter';
import ClinicSettingsListSkeleton from '../../subparts/ClinicSettingsListSkeleton';

const ManageVisitReason = ({ onBack }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [visitReasons, setVisitReasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [addingNew, setAddingNew] = useState(false);

    const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

    const getVisitReasons = async () => {
        setLoading(true);
        try {
            const res = await bookingService.getAllVisitReasons(clinicId);
            setVisitReasons(res.data || []);
        } catch (error) {
            console.log('Error fetching visit reasons:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load visit reasons. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    };

    const addVisitReason = async (data) => {
        setIsSaving(true);
        try {
            const payload = {
                reason: data.reason.trim(),
                isActive: !!data.status,
                clinicId,
            };
            const res = await bookingService.addUpdateVisitReason(clinicId, payload);
            setVisitReasons((prev) => [...prev, res.data]);
            setAddingNew(false);
            reset();
            await showSuccess({
                title: 'Reason Added',
                text: 'The visit reason was added successfully.',
            });
        } catch (error) {
            console.log('Error adding visit reason:', error);
            showError({
                title: 'Add Failed',
                text: error?.response?.data?.message || 'Unable to add the visit reason. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const updateVisitReason = async (visitReasonId, data) => {
        setIsSaving(true);
        try {
            const payload = {
                id: visitReasonId,
                reason: data.reason.trim(),
                isActive: !!data.status,
                clinicId,
            };
            const res = await bookingService.addUpdateVisitReason(clinicId, payload);
            setVisitReasons((prev) =>
                prev.map((item) =>
                    item.id === visitReasonId
                        ? { ...item, ...res.data, reason: data.reason.trim(), isActive: !!data.status }
                        : item
                )
            );
            setEditingId(null);
            reset();
            await showSuccess({
                title: 'Reason Updated',
                text: 'The visit reason was updated successfully.',
            });
        } catch (error) {
            console.log('Error updating visit reason:', error);
            showError({
                title: 'Update Failed',
                text: error?.response?.data?.message || 'Unable to update the visit reason. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const deleteVisitReason = async (reason) => {
        if (reason.isDefault) {
            showError({
                title: 'Cannot Delete',
                text: 'Default visit reasons cannot be deleted. Deactivate them instead.',
            });
            return;
        }

        const result = await confirmDelete({
            title: 'Delete Visit Reason?',
            itemName: reason.reason,
            description: 'This visit reason will be removed from web booking.',
            onConfirm: () => bookingService.deleteVisitReasonById(clinicId, reason.id),
        });

        if (result.isConfirmed) {
            setVisitReasons((prev) => prev.filter((item) => item.id !== reason.id));
        }
    };

    const handleEdit = (visitReason) => {
        setEditingId(visitReason.id);
        setAddingNew(false);
        setValue('reason', visitReason.reason);
        setValue('status', visitReason.isActive);
    };

    const handleCancel = () => {
        setEditingId(null);
        setAddingNew(false);
        reset();
    };

    const handleAddNew = () => {
        setAddingNew(true);
        setEditingId(null);
        reset({ reason: '', status: true });
    };

    useEffect(() => {
        getVisitReasons();
    }, [clinicId]);

    const renderReasonForm = (onSubmit, submitLabel) => (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-settings-record-form">
            <div className="clinic-settings-record-form__row">
                <div>
                    <label className="form-label small fw-normal text-muted">Visit Reason</label>
                    <input
                        type="text"
                        className={`form-control form-control-sm ${errors.reason ? 'is-invalid' : ''}`}
                        placeholder="Enter visit reason..."
                        {...register('reason', {
                            required: 'Visit reason is required',
                            minLength: { value: 2, message: 'Visit reason must be at least 2 characters' },
                            maxLength: { value: 255, message: 'Visit reason must be at most 255 characters' },
                        })}
                    />
                    {errors.reason && <div className="invalid-feedback">{errors.reason.message}</div>}
                </div>
            </div>
            <div className="d-flex align-items-center gap-2">
                <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" {...register('status')} defaultChecked />
                    <label className="form-check-label small">Active</label>
                </div>
            </div>
            <ClinicSettingsFormFooter isSaving={isSaving} submitLabel={submitLabel} />
        </form>
    );

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Manage Visit Reasons"
                icon="bi-chat-left-text"
                subtitle="Manage visit reasons shown on the public booking form"
                actions={(
                    <div className="d-flex gap-2 flex-wrap">
                        {!loading && (
                            addingNew ? (
                                <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
                            ) : (
                                <button type="button" className="btn btn-sm btn-light-primary rounded-pill" onClick={handleAddNew}>
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Add New Reason
                                </button>
                            )
                        )}
                        <button type="button" className="btn btn-sm btn-outline-secondary rounded-pill" onClick={onBack}>
                            <i className="bi bi-arrow-left me-1"></i>
                            Back
                        </button>
                    </div>
                )}
            />

            {loading ? (
                <ClinicInfoPanel title="Visit Reasons" icon="bi-chat-left-text">
                    <ClinicSettingsListSkeleton rows={4} />
                </ClinicInfoPanel>
            ) : (
                <ClinicInfoPanel
                    title="Visit Reasons"
                    icon="bi-chat-left-text"
                    actions={editingId ? <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} /> : null}
                >
                    {addingNew && renderReasonForm(addVisitReason, 'Add Reason')}

                    {visitReasons.length === 0 && !addingNew ? (
                        <ClinicSettingsEmpty
                            icon="bi-chat-left"
                            title="No visit reasons found"
                            description="Get started by adding your first visit reason."
                            actionLabel="Add First Reason"
                            onAction={handleAddNew}
                        />
                    ) : (
                        <div className="clinic-settings-record-list">
                            {visitReasons.map((reason) => (
                                <div
                                    key={reason.id}
                                    className={`clinic-settings-record-item ${editingId === reason.id ? 'clinic-settings-record-item--editing' : ''}`}
                                >
                                    {editingId === reason.id ? (
                                        <div className="flex-grow-1">
                                            {renderReasonForm((data) => updateVisitReason(reason.id, data), 'Save')}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="clinic-settings-record-item__avatar">
                                                <i className="bi bi-chat-left-text"></i>
                                            </span>
                                            <div className="clinic-settings-record-item__main">
                                                <h6 className="clinic-settings-record-item__title">{reason.reason}</h6>
                                                <p className="clinic-settings-record-item__meta mb-0">
                                                    {reason.isDefault ? 'Default' : 'Custom'} · Modified {reason.modifiedAt || '—'}
                                                </p>
                                            </div>
                                            <span className={`clinic-settings-status-pill ${reason.isActive ? 'is-open' : 'is-closed'}`}>
                                                {reason.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <div className="clinic-settings-record-item__actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary rounded-circle"
                                                    onClick={() => handleEdit(reason)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                                {!reason.isDefault && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger rounded-circle"
                                                        onClick={() => deleteVisitReason(reason)}
                                                        title="Delete"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ClinicInfoPanel>
            )}
        </>
    );
};

export default ManageVisitReason;
